import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api.js';
import styles from './CoverImageUploader.module.css';

/** 8MB, matching `MAX_GROUP_IMAGE_UPLOAD_BYTES` server-side. */
const MAX_COVER_IMAGE_BYTES = 8 * 1024 * 1024;

/**
 * Pick/preview/remove a circle or bolão's cover image (PLAN.md Phase 10.19).
 *
 * Deliberately doesn't know whether it's editing a circle or a bolão, or
 * whether the thing it's illustrating exists yet — it only uploads a file to
 * `/group-images` and hands the resulting URL (or `null`, on remove) back to
 * the caller via `onChange`. That keeps it usable both at creation time
 * (where the URL is just held in local state until the create call) and for
 * "edit later" (where the caller's `onChange` also fires an
 * `updateGroupImage`/`updateBolaoImage` call).
 */
export default function CoverImageUploader({
  imageUrl,
  onChange,
  disabled = false,
}: {
  imageUrl: string | null;
  onChange: (imageUrl: string | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    if (file.size > MAX_COVER_IMAGE_BYTES) {
      setError(t('coverImage.tooLarge'));
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const { url } = await api.uploadGroupImage(file);
      onChange(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('coverImage.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const busy = disabled || uploading;

  return (
    <div className={styles.uploader}>
      <button
        type="button"
        className={styles.preview}
        onClick={() => fileInputRef.current?.click()}
        disabled={busy}
        aria-label={t('coverImage.upload')}
      >
        {imageUrl ? (
          <img src={imageUrl} alt="" className={styles.previewImage} />
        ) : (
          <span className={styles.previewPlaceholder}>+</span>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void handleFile(file);
        }}
      />

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          {uploading ? t('coverImage.uploading') : t('coverImage.upload')}
        </button>
        {imageUrl ? (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => onChange(null)}
            disabled={busy}
          >
            {t('coverImage.remove')}
          </button>
        ) : null}
        {error ? <p className="error">{error}</p> : null}
      </div>
    </div>
  );
}
